import React from "react";
import { Link } from "react-router-dom";

const WHATSAPP_HREF = "https://wa.me/447724042955?text=Hi%2C%20I%27d%20like%20to%20book%20a%20free%20Discovery%20WhatsApp%20Call.";

const services = [
  {
    id: "discovery",
    eyebrow: "Free Discovery WhatsApp Call",
    title: "A quiet 15 minutes — to see if we're the right fit.",
    price: "Free",
    duration: "15 minutes · WhatsApp voice or text",
    body:
      "Before you commit to anything, a brief, no-pressure WhatsApp call (or text exchange, if a call feels too exposed) so I can hear a little of your situation and you can hear how I work. I'll tell you honestly whether 1:1 sessions are the right fit right now — and if they aren't, I'll suggest where else to look. Nothing is recorded, nothing is logged in court-safe documentation, nothing is shared.",
    inclusions: [
      "15-minute WhatsApp voice call (or text, your choice)",
      "Honest fit-check — no sales pressure",
      "A starting point recommendation, even if it isn't me",
      "Fully confidential, no records kept",
    ],
    ctaLabel: "Book free discovery call",
    ctaHref: WHATSAPP_HREF,
    external: true,
  },
  {
    id: "consultancy",
    eyebrow: "Strategy Session",
    title: "A skilled second perspective on what you're seeing.",
    price: "£180",
    duration: "75 minutes · single session",
    body:
      "A confidential, forward-looking 75-minute video session focused on the contact week ahead — what to expect, what to prepare for at home, and what to quietly log. You leave with a tailored plan for the next handover, three concrete court-safe actions, and the developmental lens to anticipate what your child is most likely to bring back through the door.",
    inclusions: [
      "75-minute video consultation, forward-looking",
      "Pre-session intake (your contact schedule + recent pattern)",
      "Tailored plan for the next contact week (court-safe language only)",
      "Written summary you can revisit before the next handover",
      "Two weeks of WhatsApp or email follow-through",
    ],
  },
  {
    id: "decoding",
    eyebrow: "Behavioural Decoding Session",
    title: "Decode the meltdown that just happened.",
    price: "£150",
    duration: "60 minutes · single session",
    body:
      "A confidential, backward-looking 60-minute session focused on a single incident — a transition meltdown, a sibling explosion, a sudden episode of panic-lying. We unpick the developmental, neurological and relational layers line by line, and leave you with the exact observational language to log it. Where the Strategy Session prepares you for the week ahead, this session helps you process what has already landed in your living room.",
    inclusions: [
      "60-minute video session, focused on one incident",
      "Developmental-stage decoder applied live",
      "Verbal-response scripts for the next 72 hours",
      "Court-safe log template, personalised",
      "One week of WhatsApp or email follow-through",
    ],
  },
  {
    id: "routine-mapping",
    eyebrow: "Custom Household Routine-Mapping",
    title: "Turn your home into the steady contrast.",
    price: "£420",
    duration: "2 × 90 minutes · one week apart",
    body:
      "A two-session deep build of your family's weekly rhythm — the most powerful, least disputable counter-balance to outside pressure. Drawn directly from years of family-support work and adapted to your child's age, neurology and contact schedule.",
    inclusions: [
      "Two 90-minute video sessions, one week apart",
      "Bespoke weekly rhythm chart (printable)",
      "Room-by-room autonomy prompts",
      "Court-record-friendly consistency log",
      "Two weeks of WhatsApp or email follow-through after the second session",
    ],
  },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="container-edit pt-16 md:pt-24 pb-24">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
        <div className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">1:1 Services</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-brand-forest leading-[0.95]">
            Private work,<br />held quietly.
          </h1>
        </div>
        <div className="md:col-span-5 md:col-start-8 self-end text-brand-charcoal leading-relaxed">
          <p>
            All sessions are independent, confidential, and outside any
            court-appointed framework. Nothing said in these sessions is
            disclosable to the other party. Limited availability.
          </p>
        </div>
      </div>

      <div className="space-y-24">
        {services.map((s, i) => (
          <article
            key={s.id}
            data-testid={`service-${s.id}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-brand-border pt-16"
          >
            <div className="md:col-span-1 font-serif text-3xl text-brand-terracotta">
              0{i + 1}
            </div>
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-muted mb-4">
                {s.eyebrow}
              </p>
              <h2 className="font-serif text-4xl tracking-tight text-brand-forest leading-tight">
                {s.title}
              </h2>
            </div>
            <div className="md:col-span-6 text-brand-charcoal leading-relaxed">
              <p>{s.body}</p>
              <ul className="mt-8 space-y-3">
                {s.inclusions.map((inc) => (
                  <li key={inc} className="flex items-start gap-3">
                    <span className="mt-2 inline-block w-5 h-px bg-brand-terracotta flex-shrink-0" />
                    <span>{inc}</span>
                  </li>
                ))}
              </ul>
              {s.external ? (
                <a
                  href={s.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-cta-${s.id}`}
                  className="mt-8 inline-flex items-center gap-2 px-7 py-3 bg-brand-terracotta text-brand-bg text-sm tracking-wide hover:bg-brand-terracottaDark transition-colors rounded-sm"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.5 3.5A11.9 11.9 0 0 0 12 0C5.4 0 0 5.4 0 12c0 2.1.6 4.2 1.6 6L0 24l6.2-1.6A11.9 11.9 0 0 0 12 24c6.6 0 12-5.4 12-12 0-3.2-1.2-6.2-3.5-8.5zM12 22c-1.9 0-3.7-.5-5.3-1.5l-.4-.2-3.7 1 1-3.6-.2-.4C2.5 15.8 2 13.9 2 12 2 6.5 6.5 2 12 2s10 4.5 10 10-4.5 10-10 10zm5.5-7.5c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-1 1.2-.2.2-.4.2-.6 0-1.4-.7-2.4-1.3-3.4-2.9-.3-.5.3-.4.7-1.4.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1 1-1 2.5 1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.2 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.5.2-.7.2-1.3.2-1.5-.1-.1-.3-.2-.5-.3z"/>
                  </svg>
                  {s.ctaLabel}
                </a>
              ) : (
                <Link
                  to={`/inquire?service=${s.id}`}
                  data-testid={`service-inquire-${s.id}`}
                  className="mt-8 inline-flex items-center px-7 py-3 border border-brand-forest text-brand-forest text-sm tracking-wide hover:bg-brand-forest hover:text-brand-bg transition-colors rounded-sm"
                >
                  Inquire about {s.eyebrow.toLowerCase()}
                </Link>
              )}

              <div className="mt-10 pt-6 border-t border-brand-border/70 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-brand-muted mb-1">Investment</p>
                  <p className="font-serif text-3xl text-brand-forest leading-none">{s.price}</p>
                </div>
                <p className="text-xs text-brand-muted">{s.duration}</p>
              </div>
              <p className="mt-4 text-xs text-brand-muted leading-relaxed italic">
                Please ask if cost is the only barrier.
              </p>
            </div>
          </article>
        ))}
      </div>

      <section className="mt-32 bg-brand-surface border border-brand-border p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <h3 className="font-serif text-3xl md:text-4xl text-brand-forest leading-tight">
            Not sure which one?
          </h3>
        </div>
        <div className="md:col-span-6 text-brand-charcoal">
          <p className="leading-relaxed">
            Tell me a little about your week and which child you are most worried
            about. I will reply personally — usually within two working days —
            with the right starting point and honest answer on whether 1:1 work
            is the right fit for you right now. Follow-up is by your choice of
            <strong className="text-brand-forest"> WhatsApp or email</strong>,
            whichever feels safest on your device.
          </p>
          <Link
            to="/inquire"
            data-testid="services-bottom-cta"
            className="mt-8 inline-flex items-center px-8 py-4 bg-brand-terracotta text-brand-bg text-sm tracking-wide hover:bg-brand-terracottaDark transition-colors rounded-sm"
          >
            Start a private inquiry
          </Link>
        </div>
      </section>
    </main>
  );
}
